import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';

import * as api from '../lib/db';
import { dayKey, prepTargetDay } from '../lib/time';
import type {
  Activity,
  Child,
  Entry,
  Family,
  FamilyCodes,
  GrowthEntry,
  Member,
  Milestone,
  Note,
  Photo,
  PrepDay,
  PrepItem,
  UserDoc,
} from '../lib/types';
import { useAuth } from './AuthContext';

/**
 * One live subscription set per family.
 *
 * Shared collections load for every member. Parent-only listeners start once
 * the current family's membership confirms a parent role; viewers never query
 * the private tracking collections.
 */

export type FamilyState = {
  /** null once loaded and the account belongs to no family yet. */
  familyId: string | null;
  family: Family | null;
  /** Invite & share codes. null for a viewer, who may not read them. */
  codes: FamilyCodes | null;
  members: Member[];
  /** The signed-in account's own membership. */
  me: Member | null;
  children: Child[];
  entries: Entry[];
  openSleeps: Entry[];
  notes: Note[];
  activity: Activity[];
  prepItems: PrepItem[];
  prepDay: PrepDay;
  growth: GrowthEntry[];
  milestones: Milestone[];
  photos: Photo[];
  /** Local-day key of the actual calendar day. */
  today: string;
  /**
   * Day the prep checklist targets by default: today until noon, tomorrow
   * after — see prepTargetDay(). `prepDay` holds this day's checks.
   */
  prepDayKey: string;
  loading: boolean;
  error: string | null;
  /** Re-read the user document, after creating or joining a family. */
  refreshUser: () => void;
};

/**
 * Exported so the dev preview harness (src/preview.tsx) can supply fixture data
 * in place of live Firestore. Application code should use `useFamily()`.
 */
export const FamilyContext = createContext<FamilyState | null>(null);

const EMPTY_PREP_DAY: PrepDay = { id: '', checks: {} };

export function FamilyProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();

  const [userDoc, setUserDoc] = useState<UserDoc | null>(null);
  const [userLoaded, setUserLoaded] = useState(false);
  const [nonce, setNonce] = useState(0);

  const [family, setFamily] = useState<Family | null>(null);
  const [codes, setCodes] = useState<FamilyCodes | null>(null);
  const [memberSnapshot, setMemberSnapshot] = useState<{ familyId: string; members: Member[] } | null>(null);
  const [kids, setKids] = useState<Child[]>([]);
  const [entries, setEntries] = useState<Entry[]>([]);
  const [openSleeps, setOpenSleeps] = useState<Entry[]>([]);
  const [notes, setNotes] = useState<Note[]>([]);
  const [activity, setActivity] = useState<Activity[]>([]);
  const [prepItems, setPrepItems] = useState<PrepItem[]>([]);
  const [prepDay, setPrepDay] = useState<PrepDay>(EMPTY_PREP_DAY);
  const [growth, setGrowth] = useState<GrowthEntry[]>([]);
  const [milestones, setMilestones] = useState<Milestone[]>([]);
  const [photos, setPhotos] = useState<Photo[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [dataLoaded, setDataLoaded] = useState(false);

  /**
   * The local day and the prep target day, kept current so an app left open
   * rolls over on its own — `today` at midnight, `prepDayKey` at midnight
   * and again at noon (when the default flips to tomorrow's bag).
   */
  const [today, setToday] = useState(() => dayKey(new Date()));
  const [prepDayKey, setPrepDayKey] = useState(() => dayKey(prepTargetDay(new Date())));
  useEffect(() => {
    const id = window.setInterval(() => {
      const now = new Date();
      const nextToday = dayKey(now);
      const nextPrep = dayKey(prepTargetDay(now));
      setToday((prev) => (prev === nextToday ? prev : nextToday));
      setPrepDayKey((prev) => (prev === nextPrep ? prev : nextPrep));
    }, 60_000);
    return () => window.clearInterval(id);
  }, []);

  // ---- which family? -------------------------------------------------------
  useEffect(() => {
    if (!user) {
      setUserDoc(null);
      setUserLoaded(true);
      return;
    }
    setUserLoaded(false);
    return api.watchUserDoc(
      user.uid,
      (value) => {
        setUserDoc(value);
        setUserLoaded(true);
      },
      (e) => {
        setError(e.message);
        setUserLoaded(true);
      },
    );
  }, [user, nonce]);

  const familyId = userDoc?.lastFamilyId ?? userDoc?.familyIds[0] ?? null;
  // Never carry a parent role over from a previous family's snapshot.
  const members = useMemo(
    () => (memberSnapshot?.familyId === familyId ? memberSnapshot.members : []),
    [memberSnapshot, familyId],
  );
  const me = useMemo(
    () => (user ? members.find((m) => m.uid === user.uid) ?? null : null),
    [members, user],
  );
  const isParent = me?.role === 'mom' || me?.role === 'dad';

  // Clear a previous family's error, not a real user-profile loading error.
  useEffect(() => setError(null), [familyId]);

  // ---- shared family data -------------------------------------------------
  useEffect(() => {
    setMemberSnapshot(null);
    setFamily(null);
    setKids([]);
    setGrowth([]);
    setMilestones([]);
    setPhotos([]);
    if (!familyId) {
      setDataLoaded(!!userLoaded);
      return;
    }

    setDataLoaded(false);
    const onError = (e: Error) => setError(e.message);

    const unsubs: api.Unsubscribe[] = [
      api.watchFamily(
        familyId,
        (value) => {
          setFamily(value);
          setDataLoaded(true);
        },
        onError,
      ),
      api.watchMembers(familyId, (value) => setMemberSnapshot({ familyId, members: value }), onError),
      api.watchChildren(familyId, setKids, onError),
      api.watchGrowth(familyId, setGrowth, onError),
      api.watchMilestones(familyId, setMilestones, onError),
      api.watchPhotos(familyId, setPhotos, onError),
    ];

    return () => unsubs.forEach((u) => u());
  }, [familyId, userLoaded]);

  // ---- private tracking data: parents only -------------------------------
  useEffect(() => {
    setEntries([]);
    setOpenSleeps([]);
    setNotes([]);
    setActivity([]);
    setPrepItems([]);
    if (!familyId || !isParent) return;

    const onError = (e: Error) => setError(e.message);
    const since = api.logWindowStart(new Date());
    const unsubs: api.Unsubscribe[] = [
      api.watchEntries(familyId, since, setEntries, onError),
      api.watchOpenSleeps(familyId, setOpenSleeps, onError),
      api.watchNotes(familyId, setNotes, onError),
      api.watchActivity(familyId, setActivity, onError),
      api.watchPrepItems(familyId, setPrepItems, onError),
    ];
    return () => unsubs.forEach((u) => u());
  }, [familyId, isParent]);

  // Prep completion is a per-day document, so it re-subscribes when the
  // default target day rolls (midnight and noon).
  useEffect(() => {
    setPrepDay(EMPTY_PREP_DAY);
    if (!familyId || !isParent) return;
    return api.watchPrepDay(familyId, prepDayKey, setPrepDay, (e) => setError(e.message));
  }, [familyId, isParent, prepDayKey]);

  /**
   * The join codes live in a parents-only document, so only subscribe once we
   * know this account is a parent — a viewer's listener would just be denied.
   */
  useEffect(() => {
    if (!familyId || !isParent) {
      setCodes(null);
      return;
    }
    return api.watchFamilyCodes(familyId, setCodes, (e) => setError(e.message));
  }, [familyId, isParent]);

  const value = useMemo<FamilyState>(
    () => ({
      familyId,
      family,
      codes,
      members,
      me,
      children: kids,
      entries,
      openSleeps,
      notes,
      activity,
      prepItems,
      prepDay,
      growth,
      milestones,
      photos,
      today,
      prepDayKey,
      loading: !userLoaded || (!!familyId && !dataLoaded),
      error,
      refreshUser: () => setNonce((n) => n + 1),
    }),
    [
      familyId,
      family,
      codes,
      members,
      me,
      kids,
      entries,
      openSleeps,
      notes,
      activity,
      prepItems,
      prepDay,
      growth,
      milestones,
      photos,
      today,
      prepDayKey,
      userLoaded,
      dataLoaded,
      error,
    ],
  );

  return <FamilyContext.Provider value={value}>{children}</FamilyContext.Provider>;
}

export function useFamily(): FamilyState {
  const ctx = useContext(FamilyContext);
  if (!ctx) throw new Error('useFamily must be used inside <FamilyProvider>');
  return ctx;
}

/**
 * The family id and the acting member, for screens that only render once both
 * exist. Throws rather than returning nulls so call sites stay readable.
 */
export function useActingMember(): { familyId: string; me: Member } {
  const { familyId, me } = useFamily();
  if (!familyId || !me) throw new Error('No active family membership');
  return { familyId, me };
}
